import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";

/**
 * Italian street/address autocomplete via Nominatim (OpenStreetMap).
 * onSelect receives {road, house_number, postcode, city, province}.
 */
export function AddressAutocomplete({ value, onChange, onSelect, city, placeholder, testid, inputClassName = "", publicMode = false }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  // True solo se l'utente ha effettivamente digitato/modificato il campo in questa sessione.
  // Impedisce che il dropdown si apra automaticamente al focus/hover quando il campo è già valorizzato.
  const interactedRef = useRef(false);

  useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 3) { setResults([]); setOpen(false); return; }
    if (!interactedRef.current) return;
    // 🆕 Se richiesta e non compilata la città, non cerchiamo (evita risultati fuori zona)
    // Nota: senza città, Nominatim restituisce risultati sparsi da tutta Italia — poco utile
    const cityTrim = (city || "").trim();
    if (!cityTrim) {
      setResults([]);
      setOpen(true); // apre popup con messaggio "Inserisci prima la città"
      return;
    }
    setLoading(true);
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      try {
        let data;
        if (publicMode) {
          const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
          const r = await axios.get(`${apiBase}/public/address`, { params: { q, city: cityTrim }, signal: ctl.signal });
          data = r.data;
        } else {
          const r = await api.get("/utils/address", { params: { q, city: cityTrim }, signal: ctl.signal });
          data = r.data;
        }
        // 🆕 Filtra strettamente per città inserita (case-insensitive substring on returned city)
        const cityLower = cityTrim.toLowerCase();
        const cityFiltered = (data || []).filter((d) => {
          if (!d.city) return true; // se il risultato non ha city, lo lasciamo passare
          return d.city.toLowerCase().includes(cityLower);
        });
        // Dedupe by road+postcode
        const seen = new Set();
        const unique = cityFiltered.filter((d) => {
          const key = `${d.road}|${d.postcode}|${d.city}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setResults(unique);
        setOpen(true);
        setHighlight(0);
      } catch (e) { logger.error("Address autocomplete failed:", e); }
      finally { setLoading(false); }
    }, 250);
    return () => { ctl.abort(); clearTimeout(t); };
  }, [value, city, publicMode]);

  const pick = (item) => {
    onChange(item.road);
    onSelect?.(item);
    setOpen(false);
    interactedRef.current = false;
  };

  const onKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(results[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <Input
        data-testid={testid}
        value={value || ""}
        onChange={(e) => { interactedRef.current = true; onChange(e.target.value); }}
        onFocus={() => { if (interactedRef.current && results.length > 0) setOpen(true); }}
        onKeyDown={onKey}
        placeholder={placeholder || "Via Roma, Corso Umberto…"}
        autoComplete="off"
        autoCapitalize="words"
        className={inputClassName}
      />
      {loading && <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-slate-400" />}
      {open && results.length === 0 && !loading && (value || "").trim().length >= 3 && interactedRef.current && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg px-3 py-2 text-xs text-slate-600">
          {(!city || !city.trim()) ? (
            <><span className="font-semibold text-amber-700">⚠️ Inserisci prima la Città</span> per vedere solo gli indirizzi di quella località.</>
          ) : (
            <>Nessun indirizzo trovato in <b>{city}</b>. Scrivi manualmente la via (es. &quot;Via Roma 12&quot;).</>
          )}
        </div>
      )}
      {loading && <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-slate-400" />}
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg scrollbar-thin" data-testid={`${testid}-list`}>
          {results.map((r, i) => (
            <button
              key={`${r.road}-${r.postcode}-${i}`}
              type="button"
              onMouseEnter={() => setHighlight(i)}
              onClick={() => pick(r)}
              className={`w-full text-left px-3 py-2 text-sm ${i === highlight ? "bg-blue-50" : "hover:bg-slate-50"}`}
            >
              <div className="flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                <span className="font-medium truncate">{r.road}{r.house_number && ` ${r.house_number}`}</span>
              </div>
              <div className="text-xs text-slate-500 ml-5 truncate">
                {r.postcode && `${r.postcode} `}{r.city}{r.province && ` (${r.province})`}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
